// src/propertyManagement/pages/ApplicantsPage.jsx
import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Typography,
  TextField
} from '@mui/material';
import {
  DataGrid,
  GridToolbar
} from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export const dummyApplicants = [
  { id: 1,  firstName: 'Priscilla', lastName: 'Branch',     unit: '1320 Bruce Street - 1',   status: 'Approved',   stage: '—',                                lastUpdated: '1/24/2025', received: '10/18/2022 12:55 PM' },
  { id: 2,  firstName: 'Jennifer',  lastName: 'Brown',      unit: '5315 Clinchport Circle - 1', status: 'Approved',  stage: '—',                                lastUpdated: '7/23/2024', received: '8/22/2023 1:41 PM' },
  { id: 3,  firstName: 'Andria',    lastName: 'Clark',      unit: '818 Laurel Street - 1',    status: 'Undecided', stage: 'Application',                     lastUpdated: '7/16/2025', received: '7/16/2025 11:39 AM' },
  { id: 4,  firstName: 'Charlene',  lastName: 'Dixon',      unit: '1640 Cherry Road - 1',     status: 'Approved',  stage: 'Screening (Reports ready)',     lastUpdated: '7/23/2024', received: '6/21/2024 11:10 AM' },
  { id: 5,  firstName: 'Jeremy',    lastName: 'Edwards',    unit: '5315 Clinchport Circle - 1', status: 'Approved',  stage: '—',                                lastUpdated: '7/23/2024', received: '10/20/2023 9:57 AM' },
  { id: 6,  firstName: 'Arnesia',   lastName: 'Henderson',  unit: '822 White Clover Lane - 1',  status: 'Approved',  stage: 'Application',                     lastUpdated: '1/13/2025', received: '—' },
  { id: 7,  firstName: 'Ashley',    lastName: 'Jeffries',   unit: '860 Inez Street - 1',      status: 'Approved',  stage: 'Screening (Reports ready)',     lastUpdated: '6/9/2025',  received: '5/28/2025 11:38 PM' },
  { id: 8,  firstName: 'Frances',   lastName: 'Jones',      unit: '797 Buntyn Street - 1',    status: 'Undecided', stage: 'Screening',                     lastUpdated: '7/10/2025', received: '—' },
  { id: 9,  firstName: 'Eugene',    lastName: 'Jones',      unit: '797 Buntyn Street - 1',    status: 'Undecided', stage: 'Screening',                     lastUpdated: '7/10/2025', received: '—' },
  { id: 10, firstName: 'Tomur',     lastName: 'Mason',      unit: '1328 Lambert Street - 1',  status: 'Undecided', stage: 'Screening (Reports ready)',     lastUpdated: '7/15/2025', received: '7/15/2025 1:21 PM' },
  { id: 11, firstName: 'Lakeisha',  lastName: 'Mckinley',   unit: '860 Inez Street - 1',      status: 'Undecided', stage: 'Screening (Reports ready)',     lastUpdated: '6/11/2025', received: '6/6/2025 2:07 PM' },
  { id: 12, firstName: 'Tempest',   lastName: 'Miller',     unit: '818 Laurel Street - 1',    status: 'Approved',  stage: '—',                                lastUpdated: '2/13/2024', received: '12/21/2022 12:45 PM' },
  { id: 13, firstName: 'Erica',     lastName: 'Newbern',    unit: '652 Gilleas Road - 1',     status: 'Approved',  stage: '—',                                lastUpdated: '8/29/2024', received: '9/6/2022 3:19 PM' },
  { id: 14, firstName: 'Phillandria', lastName: 'Patton',   unit: '773 Hilton Street - 1',    status: 'Approved',  stage: 'Screening (Reports ready)',     lastUpdated: '4/29/2025', received: '4/23/2025 11:25 AM' },
  { id: 15, firstName: 'Niamaya',   lastName: 'Penn',       unit: '840 Freeman Street - 1',    status: 'Approved',  stage: 'Screening (Reports ready)',     lastUpdated: '4/17/2025', received: '3/16/2025 7:28 PM' },
  { id: 16, firstName: 'Samantha',  lastName: 'Sims',       unit: '3464 Lamphier Avenue - 1', status: 'Approved',  stage: 'Screening (Reports ready)',     lastUpdated: '3/24/2025', received: '3/5/2025 4:52 PM' },
];

const columns = [
  { field: 'firstName',    headerName: 'First Name',        flex: 1, renderCell: params => <Link to="#" style={{ color: '#007bff', textDecoration: 'none' }}>{params.value}</Link> },
  { field: 'lastName',     headerName: 'Last Name',         flex: 1, renderCell: params => <Link to="#" style={{ color: '#007bff', textDecoration: 'none' }}>{params.value}</Link> },
  { field: 'unit',         headerName: 'Unit',              flex: 1.5 },
  { field: 'status',       headerName: 'Status',            flex: 1 },
  { field: 'stage',        headerName: 'Stages in Progress', flex: 2 },
  { field: 'lastUpdated',  headerName: 'Last Updated',      flex: 1 },
  { field: 'received',     headerName: 'Application Received', flex: 1.5 },
  {
    field: 'actions',
    headerName: '',
    width: 48,
    sortable: false,
    renderCell: () => (
      <IconButton size="small">
        <MoreVertIcon fontSize="small" />
      </IconButton>
    )
  }
];

export default function ApplicantsPage() {
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();

  // Memoize filtered rows
  const filteredRows = useMemo(() => {
    if (!searchText) return dummyApplicants;
    const lower = searchText.toLowerCase();
    return dummyApplicants.filter(row =>
      Object.values(row)
        .join(' ')
        .toLowerCase()
        .includes(lower)
    );
  }, [searchText]);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>

      {/* Header + Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5">Applicants</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="success">Add applicant</Button>
          <Button variant="outlined">Create group</Button>
          <Button variant="outlined">Order Enhanced Tenant Screening</Button>
          <IconButton><MoreVertIcon /></IconButton>
        </Box>
      </Box>

      {/* Individual/Group toggles */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="outlined" color="primary">By individual</Button>
        <Button variant="text">By group</Button>
      </Box>

      {/* Global Search */}
      <div style={{width:'25%'}}>
<TextField
        label="Search Applicants"
        variant="outlined"
        size="small"
        fullWidth
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
      />
      </div>
      

      {/* DataGrid */}
      <Box sx={{ flexGrow: 1 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSize={10}
                  onRowClick={(params) => navigate(`/pm/applications/${params.id}`)}

          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          checkboxSelection={false}
          components={{ Toolbar: GridToolbar }}
          autoHeight={false}
          sx={{
            '& .MuiDataGrid-main': { border: 'none' },
            '& .MuiDataGrid-toolbarContainer': { display: 'none' }  // hide built-in toolbar if you like
          }}
        />
      </Box>
    </Box>
  );
}